import {
  validatedPropertyInterestDataSchema,
  type Location,
  type PropertyFiltersType,
  type PropertyInterestData,
} from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import z from 'zod/v4'
import { createFlowPhases, FlowProvider, type FlowData } from '~/components/flows/FlowProvider'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { useAlert } from '~/components/GlobalAlert'
import { ConfigureFeedbackInterest } from '~/components/interests/ConfigureFeedbackInterest'
import { PropertyInterestDescription } from '~/components/interests/FeedbackInterestQuestions'
import { InterestDetailsView } from '~/components/interests/InterestDetailsView'
import { SectionTitle } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import { Body } from '~/components/typography/Content'
import { getContactInterestQueryKey, useContactInterest } from '~/data/interests/useContactInterest'
import { propertyFilterFormSchema } from '~/lib/propertyFormSchemas'
import { trpc } from '~/lib/trpc'
import { cn } from '~/lib/utils'

interface ContactInterestFlowProps {
  assetId?: string
  contactId: string
  onComplete: (interestData: PropertyInterestData) => void
  onClose: () => void
  isInBottomSheet?: boolean
  className?: string
}

const contactInterestFlowDataSchema = z.object({
  propertyInterest: validatedPropertyInterestDataSchema.pick({
    filters: true,
    locations: true,
  }),
  interestDescription: validatedPropertyInterestDataSchema.pick({
    description: true,
  }),
})

type ContactInterestFlowData = z.infer<typeof contactInterestFlowDataSchema>

export function ContactInterestFlow({
  contactId,
  onComplete,
  onClose,
  isInBottomSheet = false,
  className,
}: ContactInterestFlowProps) {
  const { t } = useTranslations(['interest', 'common'])
  const { showAlert } = useAlert()
  const { data: interestStatus, isLoading: isLoadingInterest } = useContactInterest(contactId)

  const queryClient = useQueryClient()

  const submitInterestMutation = useMutation(
    trpc.feedback.submitContactInterest.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getContactInterestQueryKey(contactId),
        })
      },
    })
  )

  const existingInterest = interestStatus?.realtorInterest

  const initialData = useMemo(
    () => ({
      propertyInterest: {
        answers: existingInterest?.data.answers ?? [],
        filters: existingInterest?.data.filters ?? {},
        locations: existingInterest?.data.locations ?? [],
      },
      interestDescription: {
        description: existingInterest?.description ?? null,
      },
    }),
    [existingInterest]
  )

  const phases = useMemo(
    () =>
      createFlowPhases([
        {
          id: 'propertyInterest' as const,
          title: t(($) => $.flow.contactInterestTitle),
          schema: validatedPropertyInterestDataSchema.pick({
            filters: true,
            locations: true,
          }),
          data: initialData?.propertyInterest,
          usesCustomButtons: true,
        },
        {
          id: 'interestDescription' as const,
          title: t(($) => $.flow.additionalInfoTitle),
          schema: validatedPropertyInterestDataSchema.pick({
            description: true,
          }),
          data: initialData?.interestDescription,
          usesCustomButtons: true,
        },
      ] as const),
    [initialData?.interestDescription, initialData?.propertyInterest, t]
  )

  const onCompleteFlow = useCallback(
    async (data: FlowData) => {
      const parsedFlow = contactInterestFlowDataSchema.safeParse(data)

      if (!parsedFlow.success) {
        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description: t(($) => $.errors.validationFailed),
        })
        return false
      }

      const interestData: PropertyInterestData = {
        answers: existingInterest?.data.answers ?? [],
        filters: parsedFlow.data.propertyInterest.filters,
        locations: parsedFlow.data.propertyInterest.locations,
        description: parsedFlow.data.interestDescription.description ?? null,
      }

      try {
        const result = await submitInterestMutation.mutateAsync({
          contactId,
          interest: interestData,
        })

        if (!result.success) {
          let errorDescription = t(($) => $.errors.saveFailed) as string

          if (result.error === 'UNAUTHORIZED') {
            errorDescription = t(($) => $.errors.unauthorized)
          } else if (result.error === 'CONTACT_NOT_FOUND') {
            errorDescription = t(($) => $.errors.contactNotFound)
          } else if (result.error === 'VERIFIED_INTEREST_EXISTS') {
            errorDescription = t(($) => $.errors.verifiedInterestExists)
          }

          showAlert({
            type: 'error',
            title: t(($) => $.errors.title),
            description: errorDescription,
          })
          return false
        }

        onComplete(interestData)
        return true
      } catch (error) {
        console.error('Failed to submit contact interest', error)
        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description: t(($) => $.errors.saveFailed),
        })
        return false
      }
    },
    [contactId, showAlert, submitInterestMutation, onComplete, t]
  )

  const isNextDisabled = useCallback((data: FlowData, currentPhaseId: string) => {
    if (currentPhaseId === 'propertyInterest') {
      const interestData = data?.propertyInterest as
        | { filters?: unknown; locations?: unknown }
        | undefined

      const structureValid = validatedPropertyInterestDataSchema
        .pick({ filters: true, locations: true })
        .safeParse(interestData).success

      if (!structureValid) {
        return true
      }

      return !propertyFilterFormSchema.safeParse(interestData?.filters).success
    }

    if (currentPhaseId === 'interestDescription') {
      const description = (data?.interestDescription as { description?: string | null } | undefined)
        ?.description

      if (!description || typeof description !== 'string') {
        return true
      }

      const trimmed = description.trim()
      return trimmed.length < 10 || trimmed.length > 500
    }

    return false
  }, [])

  if (isLoadingInterest) {
    return <LoadingView />
  }

  if (interestStatus?.verifiedInterest) {
    return (
      <View className={className}>
        <View className="gap-4 px-5 py-6">
          <SectionTitle>{t(($) => $.flow.verifiedInterestTitle)}</SectionTitle>
          <Body>{t(($) => $.flow.verifiedInterestDescription)}</Body>
          <InterestDetailsView
            interest={interestStatus.verifiedInterest.data}
            className={cn('rounded-xl border border-border bg-white')}
          />
        </View>
      </View>
    )
  }

  return (
    <FlowProvider
      flowId="contact-interest"
      phases={phases}
      flowTitle={t(($) => $.flow.contactInterestTitle)}
      onClose={(resetFlow) => {
        resetFlow()
        onClose()
      }}
      onComplete={onCompleteFlow}
      isNextDisabled={isNextDisabled}
      isLoading={submitInterestMutation.isPending}
      isInBottomSheet={isInBottomSheet}
      initialData={initialData}
      showResetButton
      completeLabel={t(($) => $.common.navigation.complete)}
      className={className}>
      <ContactInterestFlowContent />
    </FlowProvider>
  )
}

function ContactInterestFlowContent() {
  const { t } = useTranslations('interest')
  const { data: flowData, currentPhaseId, updateData, nextPhase, previousPhase } = useFlowManager()

  const handleFiltersChange = useCallback(
    (
      nextFilters: PropertyFiltersType | ((current: PropertyFiltersType) => PropertyFiltersType)
    ) => {
      // eslint-disable-next-line i18next/no-literal-string
      updateData('propertyInterest', (current) => {
        const currentInterest =
          (current as Partial<ContactInterestFlowData>['propertyInterest']) ?? {
            filters: {},
          }

        const currentFilters = currentInterest.filters as PropertyFiltersType | undefined

        return {
          ...currentInterest,
          filters:
            typeof nextFilters === 'function' ? nextFilters(currentFilters ?? {}) : nextFilters,
        }
      })
    },
    [updateData]
  )

  const handleLocationsChange = useCallback(
    (locations: Location[] | ((current: Location[]) => Location[])) => {
      // eslint-disable-next-line i18next/no-literal-string
      updateData('propertyInterest', (current) => {
        const currentInterest =
          (current as Partial<ContactInterestFlowData>['propertyInterest']) ?? {
            filters: {},
            locations: [],
          }

        const currentLocations = currentInterest.locations as Location[] | undefined

        return {
          ...currentInterest,
          locations:
            typeof locations === 'function' ? locations(currentLocations ?? []) : locations,
        }
      })
    },
    [updateData]
  )

  const handleDescriptionChange = useCallback(
    (description: string) => {
      // eslint-disable-next-line i18next/no-literal-string
      updateData('interestDescription', (current) => {
        return {
          ...current,
          description,
        }
      })
    },
    [updateData]
  )

  if (currentPhaseId === 'propertyInterest') {
    return (
      <View className="flex-1">
        <ConfigureFeedbackInterest
          interestData={{
            ...(flowData?.['propertyInterest'] as ContactInterestFlowData['propertyInterest']),
          }}
          onChangeFilters={handleFiltersChange}
          onChangeLocations={handleLocationsChange}
          isForContact
        />
      </View>
    )
  }

  if (currentPhaseId === 'interestDescription') {
    return (
      <PropertyInterestDescription
        flowData={flowData as Partial<ContactInterestFlowData>}
        onUpdateDescription={handleDescriptionChange}
        onNext={nextPhase}
        onPrevious={previousPhase}>
        <View className="mt-4 px-3">
          <Body>{t(($) => $.flow.additionalInfoDescription)}</Body>
        </View>
      </PropertyInterestDescription>
    )
  }

  return null
}
